import { IAjaxOptions, IAjax } from "../provider/provider-interfaces";
import { ErrorHandler } from "../error-handler";
import { PromiseHelper, ICancelablePromise } from "../promise-helper";
import { UrlHelper } from "../html/url-helper";
import { LoadVm } from "../../loading/loadvm";
import { Logger } from "../../logger/logger";
import { GlobalLabels } from "../settings/global-labels";

const AjaxDefaultOptions: IAjaxOptions = {
	url: null,
	method: "GET",
	cache: false,
	timeout: 0,
	params: {}
};

const AjaxOptionBuilder = (options: Partial<IAjaxOptions>, defaultOptions?: IAjaxOptions) => {
	return $.extend(true, {}, defaultOptions || AjaxDefaultOptions, options);
};

let makeCancelablePromise = (xhr: JQuery.jqXHR): ICancelablePromise<any> => {
	let promise = (async () => {
		try {
			return await xhr.promise();
		} catch (e) {
			let wasAborted = e.readyState === 0 || e.statusText === "abort" || e.statusText === GlobalLabels.abortText;
			if (wasAborted && LoadVm.isRedirecting) {
				Logger.warnWithTrace("Ignoring abort during redirect", e);
				await new Promise(() => { }); // never resolve this error
			} else {
				throw e;
			}
		}
	})();

	return PromiseHelper.cancelable(promise, () => { xhr.abort(GlobalLabels.abortText); })
};

export const enum ReadyState {
	Unsent = 0,
	Opened = 1,
	HeadersReceived = 2,
	Loading = 3,
	Done = 4
}

let dataAsync = (options: IAjaxOptions) => {
	let mergedOptions = AjaxOptionBuilder(options);
	let sendOptions = {
		url: mergedOptions.url,
		data: mergedOptions.params,
		type: mergedOptions.method,
		contentType: "multipart/form-data",
		dataType: options.dataType || "json",
		cache: mergedOptions.cache,
		timeout: mergedOptions.timeout
	};

	if (mergedOptions.method === "GET") {
		// tslint:disable-next-line:ban
		return makeCancelablePromise($.get(sendOptions));
	} else {
		// tslint:disable-next-line:ban
		return makeCancelablePromise($.post(sendOptions));
	}
};

let ajaxCallList: any[] = [];
let jsonAsync = (options: IAjaxOptions) => {
	let mergedOptions = AjaxOptionBuilder(options);

	let data: any = mergedOptions.params ?? {};
	data.v = UrlHelper.getCacheId();

	// NOTE: to post a json object it must be converted to a json string
	let type = mergedOptions.method.toUpperCase();
	if (type === "POST" || type === "PATCH" || type === "DELETE") {
		data = JSON.stringify(mergedOptions.params);
	}

	// tslint:disable-next-line:ban
	let ajaxPromise = $.ajax({
		url: mergedOptions.url,
		data,
		type: mergedOptions.method,
		contentType: "application/json",
		dataType: "json",
		timeout: mergedOptions.timeout
	});

	ajaxCallList.push(ajaxPromise.promise());
	return makeCancelablePromise(ajaxPromise);
};

let jsonIdleAsync = async (options: IAjaxOptions) => {
	await Promise.all(ajaxCallList).catch(ErrorHandler.log());
	ajaxCallList.length = 0;
	return jsonAsync(options);
};

export const ajax: IAjax = {
	dataAsync,
	jsonAsync,
	getJsonCacheAsync: (options: IAjaxOptions) => {
		let getOptions = AjaxOptionBuilder({ method: "GET", cache: true }, options);
		return jsonAsync(getOptions);
	},
	getJsonNoCacheAsync: (options: IAjaxOptions) => {
		let getOptions = AjaxOptionBuilder({ method: "GET", cache: false }, options);
		return jsonAsync(getOptions);
	},
	getJsonNoCacheOnIdleAsync: (options: IAjaxOptions) => {
		let getOptions = AjaxOptionBuilder({ method: "GET", cache: false }, options);
		return jsonIdleAsync(getOptions);
	},
	postJsonAsync: (options: IAjaxOptions) => {
		let postOptions = AjaxOptionBuilder({ method: "POST" }, options);
		return jsonAsync(postOptions);
	},
	patchJsonAsync: (options: IAjaxOptions) => {
		let patchOptions = AjaxOptionBuilder({ method: "PATCH" }, options);
		return jsonAsync(patchOptions);
	},
	postDataAsync: (options: IAjaxOptions) => {
		let postOptions = AjaxOptionBuilder({ method: "POST" }, options);
		return dataAsync(postOptions);
	},
	makeCancelablePromise
};